import { Banknote, CheckCircle2, Clock3, FileSearch, Hourglass, Inbox, Percent, Users, XCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../../components/Spinner";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp, money, relativeTime } from "../../../lib/format";
import { formatMxn } from "../../../lib/loans";
import { useInvalidateAll, useLendingOverview } from "../../../lib/queries";
import type { LendingOverview } from "../../../types";
import { LoanStatusTag } from "../../loan/LoanShared";
import { ConfirmButton, Feedback, PageError, Pill, StatTile, compactMoney, hoursLabel, percent, useAction } from "../shared";
import { LENDING_BASE, LendingFrame } from "./LendingShared";

export function AdminLendingOverviewPage() {
  const overview = useLendingOverview();
  return (
    <LendingFrame title="Peso a Peso" subtitle="Viaja ahora, paga después: cartera de solicitudes, revisores y despliegue del módulo.">
      {overview.isLoading && <div className="page-state"><Spinner label="Cargando" /></div>}
      {overview.isError && <PageError title="No pudimos cargar el resumen" retry={() => void overview.refetch()} />}
      {overview.data && <Body data={overview.data} />}
    </LendingFrame>
  );
}

function Body({ data }: { data: LendingOverview }) {
  const { byStatus, monthly, byTerm, reviewers, recent, oldestOpen, settings } = data;
  const open = byStatus.submitted.count + byStatus.in_review.count;
  const requested = Object.values(byStatus).reduce((sum, item) => sum + item.amount, 0);
  const activeReviewers = reviewers.filter((reviewer) => reviewer.status === "active");
  return (
    <div className="admin-stack">
      <RolloutCard settings={settings} />

      {oldestOpen && (
        <Link to={`${LENDING_BASE}/solicitudes/${oldestOpen.id}`} className={`attention-row ${oldestOpen.waitingHours > 72 ? "danger" : oldestOpen.waitingHours > 24 ? "warning" : "info"} standalone`}>
          <span className="attention-icon"><Hourglass size={16} aria-hidden="true" /></span>
          <span className="attention-copy">
            <strong>La solicitud más antigua sin resolver lleva {hoursLabel(oldestOpen.waitingHours)} esperando</strong>
            <span>{oldestOpen.folio} · {oldestOpen.applicant} · {formatMxn(oldestOpen.amount)} · enviada {formatTimestamp(oldestOpen.submittedAt)}</span>
          </span>
        </Link>
      )}

      <div className="stat-grid four">
        <StatTile label="Por resolver" value={open} hint={`${byStatus.submitted.count} nuevas · ${byStatus.in_review.count} en revisión`} icon={Inbox} tone={open > 0 ? "alert" : "default"} to={`${LENDING_BASE}/solicitudes`} />
        <StatTile label="Aprobadas" value={byStatus.approved.count} hint={compactMoney(byStatus.approved.amount)} icon={CheckCircle2} to={`${LENDING_BASE}/solicitudes?estatus=approved`} />
        <StatTile label="Rechazadas" value={byStatus.rejected.count} hint={compactMoney(byStatus.rejected.amount)} icon={XCircle} to={`${LENDING_BASE}/solicitudes?estatus=rejected`} />
        <StatTile label="Tasa de aprobación" value={percent(data.approvalRate)} hint={data.approvalRate === null ? "sin decisiones aún" : "sobre las resueltas"} icon={Percent} />
      </div>
      <div className="stat-grid four">
        <StatTile label="Solicitado en total" value={compactMoney(requested)} hint={`${data.total} solicitudes · ${data.applicants} solicitantes`} icon={Banknote} />
        <StatTile label="Monto promedio" value={data.averageAmount === null ? "—" : compactMoney(data.averageAmount)} icon={FileSearch} />
        <StatTile label="Tiempo de decisión" value={hoursLabel(data.medianDecisionHours)} hint={data.averageDecisionHours === null ? "sin decisiones aún" : `mediana · promedio ${hoursLabel(data.averageDecisionHours)}`} icon={Clock3} />
        <StatTile label="Últimos 30 días" value={data.last30Days} hint={`${data.last7Days} en la última semana`} icon={Users} />
      </div>

      <div className="admin-two-col">
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Tendencia</span><h2>Solicitudes por mes</h2></div><span>Últimos seis meses</span></div>
          <MonthlyTable rows={monthly} />
          {byTerm.length > 0 && (
            <>
              <span className="rail-title" style={{ marginTop: 18, display: "block" }}>Por plazo</span>
              <div className="split-figures">
                {byTerm.map((term) => (
                  <div key={term.termMonths}><span>{term.termMonths} meses</span><strong>{term.count}</strong><small>{compactMoney(term.amount)} · {term.approved} aprobadas</small></div>
                ))}
              </div>
            </>
          )}
        </section>
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Actividad</span><h2>Últimos movimientos</h2></div><Link to={`${LENDING_BASE}/solicitudes`} className="text-link">Ver cola</Link></div>
          {recent.length === 0 && <p className="rail-empty">Todavía no hay solicitudes.</p>}
          <div className="recent-list">
            {recent.map((item) => (
              <Link key={item.id} to={`${LENDING_BASE}/solicitudes/${item.id}`} className="recent-row">
                <div><strong translate="no">{item.folio}</strong><span>{item.applicant} · {item.termMonths} meses</span></div>
                <div className="recent-right"><strong translate="no">{money.format(item.amount)}</strong><span><LoanStatusTag status={item.status} audience="partner" /> {relativeTime(item.decidedAt ?? item.submittedAt)}</span></div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <section className="card">
        <div className="section-heading small"><div><span className="page-eyebrow">Revisores</span><h2>Equipo de Peso a Peso</h2></div><Link to={`${LENDING_BASE}/aliados`} className="text-link">Administrar accesos</Link></div>
        {reviewers.length === 0 && <p className="rail-empty">Aún no hay revisores dados de alta. Crea el primero en «Revisores».</p>}
        {reviewers.length > 0 && (
          <div className="table-wrap flat">
            <table className="admin-table compact">
              <thead><tr><th>Revisor</th><th>Estado</th><th>Decisiones</th><th>Última decisión</th><th>Último acceso</th></tr></thead>
              <tbody>
                {reviewers.map((reviewer) => (
                  <tr key={reviewer.id}>
                    <td><Link to={`/admin/usuarios/${reviewer.id}`} className="table-primary"><strong>{reviewer.fullName}</strong><span>{reviewer.email}</span></Link></td>
                    <td><Pill status={reviewer.status} /></td>
                    <td>{reviewer.decisions}{reviewer.reviews > reviewer.decisions ? ` · ${reviewer.reviews - reviewer.decisions} en revisión` : ""}</td>
                    <td>{reviewer.lastDecisionAt ? relativeTime(reviewer.lastDecisionAt) : "—"}</td>
                    <td>{reviewer.lastLoginAt ? relativeTime(reviewer.lastLoginAt) : "Nunca"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="field-hint">{activeReviewers.length} acceso(s) activo(s). Aventuria no aprueba créditos: cada decisión aquí la registró Peso a Peso.</p>
      </section>
    </div>
  );
}

function MonthlyTable({ rows }: { rows: LendingOverview["monthly"] }) {
  const max = Math.max(1, ...rows.map((row) => row.submitted));
  const label = (month: string) => {
    const [year, m] = month.split("-").map(Number);
    return new Date(year!, m! - 1, 1).toLocaleDateString("es-MX", { month: "short", year: "2-digit" });
  };
  return (
    <div className="month-bars" role="table" aria-label="Solicitudes por mes">
      {rows.map((row) => (
        <div key={row.month} className="month-row" role="row">
          <span className="month-label" role="cell">{label(row.month)}</span>
          <span className="month-track" role="cell" aria-label={`${row.submitted} solicitudes, ${row.approved} aprobadas`}>
            <span className="month-bar" style={{ width: `${(row.submitted / max) * 100}%` }}>
              <span className="month-bar-approved" style={{ width: row.submitted ? `${(row.approved / row.submitted) * 100}%` : "0%" }} />
            </span>
          </span>
          <span className="month-value" role="cell" translate="no"><strong>{row.submitted}</strong><small>{row.approved} aprob. · {compactMoney(row.submittedAmount)}</small></span>
        </div>
      ))}
      <div className="flow-legend" aria-hidden="true"><span><i className="swatch one" /> Enviadas</span><span><i className="swatch two" /> Aprobadas</span></div>
    </div>
  );
}

/** The rollout switch, right where the partnership is managed. The full setting is re-sent so nothing else changes. */
function RolloutCard({ settings }: { settings: LendingOverview["settings"] }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [pending, setPending] = useState(false);
  const everyone = settings.audience === "all";
  const flip = async () => {
    setPending(true);
    try {
      await action.run("audience", async () => {
        await apiRequest("/api/v1/admin/settings/tnpl", { method: "PUT", body: JSON.stringify({ ...settings, audience: everyone ? "admins" : "all" }) });
        await invalidate();
        return everyone ? "El módulo vuelve a ser visible solo para administradores." : "«Viaja ahora, paga después» quedó disponible para todos los clientes.";
      });
    } finally { setPending(false); }
  };
  return (
    <section className={`rollout-card ${everyone ? "live" : "pilot"}`}>
      <div>
        <span className="rail-title">Despliegue del módulo</span>
        <h2>{everyone ? "Disponible para todos los clientes" : "Piloto: solo administradores lo ven"}</h2>
        <p className="field-hint">
          {everyone ? "Cualquier cuenta activa puede solicitar un crédito." : "Los clientes ven «Disponible próximamente»."}
          {` Tasa de referencia ${settings.annualRatePercent}% · montos de ${money.format(settings.minAmount)} a ${money.format(settings.maxAmount)}.`}
          {" "}<Link to={`${LENDING_BASE}/configuracion`}>Editar textos y límites</Link>
        </p>
        <Feedback error={action.error} notice={action.notice} />
      </div>
      <ConfirmButton
        label={everyone ? "Volver a piloto" : "Abrir a todos los clientes"}
        title={everyone ? "Cerrar el módulo a los clientes" : "Abrir «Viaja ahora, paga después» a todos"}
        message={everyone
          ? <p>Los clientes dejarán de ver el formulario de crédito y verán «Disponible próximamente». Las solicitudes ya enviadas no se afectan.</p>
          : <p>Todas las cuentas activas podrán enviar solicitudes de crédito a Peso a Peso desde ese momento. Asegúrate de que el equipo de revisores esté listo.</p>}
        confirmLabel={everyone ? "Volver a piloto" : "Abrir a todos"}
        onConfirm={flip}
        disabled={pending}
      />
    </section>
  );
}
